import type { FeatureFlagKey } from '../registry/feature-flags';

/**
 * Provider-agnostic context for flag evaluation
 * Used to determine which flags are enabled for a given user/tenant/org
 */
export type FlagContext = {
  tenantId?: string;
  userId?: string;
  orgId?: string;
  roleId?: string;
  environment?: string;
  [key: string]: string | number | boolean | undefined;
};

/**
 * Result of a boolean flag evaluation (provider-agnostic)
 */
export type BooleanEvaluationResult = {
  enabled: boolean;
  flagKey: FeatureFlagKey;
  reason: string;
  context?: FlagContext;
  timestamp?: number;
};

/**
 * Result of a variant flag evaluation (provider-agnostic)
 */
export type VariantEvaluationResult = {
  flagKey: FeatureFlagKey;
  match: boolean;
  variantKey: string;
  variantAttachment?: string;
  reason: string;
  context?: FlagContext;
  timestamp?: number;
};

/**
 * Union type for flag evaluation results
 * Can be either a boolean or variant evaluation result
 *
 * Note: This replaces the deprecated BooleanEvaluationResult-only alias
 */
export type FlagEvaluationResult =
  | BooleanEvaluationResult
  | VariantEvaluationResult;

/**
 * Type guard to check if a result is a boolean evaluation
 * Checks for 'enabled' property and absence of 'variantKey' to ensure mutual exclusivity
 */
export function isBooleanEvaluationResult(
  result: FlagEvaluationResult,
): result is BooleanEvaluationResult {
  return 'enabled' in result && !('variantKey' in result);
}

/**
 * Type guard to check if a result is a variant evaluation
 * Checks for 'variantKey' property (variant results don't have 'enabled')
 */
export function isVariantEvaluationResult(
  result: FlagEvaluationResult,
): result is VariantEvaluationResult {
  return 'variantKey' in result;
}

/**
 * Request for batch flag evaluation
 */
export type BatchEvaluationRequest = {
  flags: FeatureFlagKey[];
  context: FlagContext;
};

/**
 * Response from batch flag evaluation
 * Maps flag keys to their evaluation results (can be boolean or variant)
 */
export type BatchEvaluationResponse = Record<string, FlagEvaluationResult>;

/**
 * Options for flag evaluation
 */
export type EvaluationOptions = {
  defaultValue?: boolean;
  cacheEnabled?: boolean;
  cacheTTL?: number; // in milliseconds
};

/**
 * Feature flag provider interface - implement this for each provider
 */
export type FeatureFlagProvider = {
  /** Provider name for logging/debugging */
  readonly name: string;

  /** Initialize the provider */
  init(): Promise<void>;

  /** Check if provider is ready */
  isReady(): boolean;

  /** Evaluate a boolean flag */
  evaluateBoolean(
    flagKey: FeatureFlagKey,
    entityId: string,
    context: Record<string, string>,
  ): Promise<{ enabled: boolean; reason: string }>;

  /** Evaluate a variant flag */
  evaluateVariant(
    flagKey: FeatureFlagKey,
    entityId: string,
    context: Record<string, string>,
  ): Promise<{
    match: boolean;
    variantKey: string;
    variantAttachment?: string;
    reason: string;
  }>;

  /** Get initialization error if any */
  getError(): Error | null;

  /** Cleanup/close provider resources */
  close(): Promise<void>;
};

/**
 * Provider configuration (internal, provider-specific configs extend this)
 */
export type ProviderConfig = {
  environment?: string;
};

/**
 * Flipt-specific configuration
 */
export type FliptProviderConfig = ProviderConfig & {
  url: string;
  token?: string;
  namespace?: string;
  /** Refresh interval for flag snapshot (in seconds) */
  interval?: string;
};
